import { defineStore } from "pinia";
import { computed, watch } from "vue";
import { useSelector } from "@xstate/vue";

import { authMachine } from "@/machines/auth-machine";
import { recordReload } from "@/services/ReloadDiagnostics";

import type { ApplicationMosaic } from "@recordsure/unity-auth-machine";

const USER_OPT_INS_KEY = "unity_mosaic_optins";

export const useMosaicStore = defineStore("mosaic", () => {
  const mosaics = useSelector(authMachine, (snapshot) => snapshot.context.user?.mosaics ?? []);
  const authenticated = useSelector(authMachine, (snapshot) => snapshot.matches("authenticated"));

  const mosaicsAvailableToOptIn = computed(() => {
    const result = new Map<string, string>();

    for (const mosaic of mosaics.value) {
      // the mosaic mustn't be live to be available for opt-in, otherwise they're auto selected
      if (mosaic.isLive) continue;
      // SSO APIs from before the label column don't send one, so fall back to the name
      result.set(mosaic.name, mosaic.label != null && mosaic.label.length > 0 ? mosaic.label : mosaic.name);
    }

    return result;
  });

  const activeStack = computed(() => {
    let storedList = new Set();

    try {
      const parsedValue = JSON.parse(localStorage.getItem(USER_OPT_INS_KEY) ?? "[]") as unknown;
      storedList = new Set(Array.isArray(parsedValue) ? parsedValue : undefined);
    } catch (error) {
      console.warn(`Unable to parse ${USER_OPT_INS_KEY}`, error);
    }

    const userOptIns = new Set<string>();
    const list: ApplicationMosaic[] = [];

    for (const mosaic of mosaics.value) {
      const name = mosaic.name;
      if (mosaicsAvailableToOptIn.value.has(name) && storedList.has(name)) userOptIns.add(name);
      if (!mosaicsAvailableToOptIn.value.has(name) || storedList.has(name)) list.push(mosaic);
    }

    // The resolver uses the first match: previews take priority over live fallbacks, then higher orders win.
    list.sort((left, right) => {
      // move live mosaics to the end of the list as the new fallbacks
      if (left.isLive && !right.isLive) return 1;
      if (!left.isLive && right.isLive) return -1;

      return right.order - left.order;
    });

    return { userOptIns, list };
  });

  // reload page when the order changes
  watch([authenticated, activeStack], ([, _new], [previouslyAuthenticated, _old]) => {
    if (!authenticated.value) return;

    const max = Math.max(_new.list.length, _old.list.length) - 1;
    for (let i = 0; i <= max; i++) {
      if (_new.list[i]?.name === _old.list[i]?.name) continue;

      recordReload("mosaic-stack-changed", {
        previousMosaics: _old.list,
        nextMosaics: _new.list,
        previouslyAuthenticated,
      });
      window.location.reload();
      break;
    }
  });

  function dispatchActiveStack(action: { type: "IN" | "OUT"; name: string } | { type: "CLEAR" }) {
    let result = activeStack.value.userOptIns;
    switch (action.type) {
      case "IN": {
        if (activeStack.value.userOptIns.has(action.name)) return;
        result = new Set(activeStack.value.userOptIns).add(action.name);
        break;
      }
      case "OUT": {
        if (!activeStack.value.userOptIns.has(action.name)) return;
        result = new Set(activeStack.value.userOptIns);
        result.delete(action.name);
        break;
      }
      case "CLEAR": {
        if (activeStack.value.userOptIns.size === 0) return;
        result = new Set();
        break;
      }
    }

    if (result.size === 0) localStorage.removeItem(USER_OPT_INS_KEY);
    else localStorage.setItem(USER_OPT_INS_KEY, JSON.stringify([...result]));

    // aside from refreshing each UI component, this is to ensure the `userOptIns` computed property (dependent on the localStorage) is re-calculated
    recordReload("mosaic-opt-in-changed", { action: action.type });
    window.location.reload();
  }

  return { activeStack, mosaicsAvailableToOptIn, dispatchActiveStack };
});
